(() => {
  'use strict';

  const SELECTOR = '.lens-diagram-wrapper';
  const initializedWrappers = new WeakSet();

  function initAll(root = document) {
    const wrappers = [];

    if (root instanceof Element && root.matches(SELECTOR)) {
      wrappers.push(root);
    }

    root.querySelectorAll?.(SELECTOR).forEach((wrapper) => wrappers.push(wrapper));
    wrappers.forEach(initLensDiagram);
  }

  function initLensDiagram(wrapper) {
    if (initializedWrappers.has(wrapper)) return;

    const raw = wrapper.dataset.modelDesc?.trim();
    const defaultUnit = wrapper.dataset.defaultUnit || 'cm';
    const trigger = wrapper.querySelector('[data-lens-open]');
    const summary = wrapper.querySelector('[data-lens-summary]');
    const modal = wrapper.querySelector('.lens-diagram-modal');
    const dialog = modal?.querySelector('[role="dialog"]');

    if (!raw || !trigger || !summary || !modal || !dialog) {
      wrapper.hidden = true;
      return;
    }

    const values = parseDimensions(raw, defaultUnit);

    if (!values.hasAny) {
      console.warn('[glasses-diagram] Metafield em formato inesperado:', raw);
      wrapper.hidden = true;
      return;
    }

    if (!values.isComplete) {
      showSummaryOnly(wrapper, trigger, summary, modal, values);
      initializedWrappers.add(wrapper);
      return;
    }

    updateDiagram(modal, values.dimensions);
    initializedWrappers.add(wrapper);

    trigger.addEventListener('click', () => openModal(wrapper, modal, dialog, trigger));

    modal.querySelectorAll('[data-lens-close]').forEach((element) => {
      element.addEventListener('click', () => closeModal(modal, trigger));
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal(modal, trigger);
        return;
      }

      if (event.key === 'Tab') {
        trapFocus(event, dialog);
      }
    });
  }

  function openModal(wrapper, modal, dialog, trigger) {
    document.querySelectorAll('.lens-diagram-modal:not([hidden])').forEach((openModalElement) => {
      if (openModalElement !== modal) {
        const openTrigger = document.querySelector(`[aria-controls="${CSS.escape(openModalElement.id)}"]`);
        closeModal(openModalElement, openTrigger);
      }
    });

    wrapper.dataset.previousFocus = 'trigger';
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('lens-diagram-is-open');

    requestAnimationFrame(() => {
      const closeButton = modal.querySelector('.lens-diagram-modal__close');
      (closeButton || dialog).focus({ preventScroll: true });
    });
  }

  function closeModal(modal, trigger) {
    if (!modal || modal.hidden) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');

    if (!document.querySelector('.lens-diagram-modal:not([hidden])')) {
      document.body.classList.remove('lens-diagram-is-open');
    }

    trigger?.focus({ preventScroll: true });
  }

  function parseDimensions(text, defaultUnit = 'cm') {
    const segments = text
      .split(/\s*(?:\||;|\n)\s*/)
      .map((segment) => segment.trim())
      .slice(0, 5);

    while (segments.length < 5) segments.push('');

    const millimetres = segments.map((segment) => extractMillimetres(segment, defaultUnit));

    if (false) {
      console.warn('[glasses-diagram] Não foi possível extrair todos os valores.', segments);
      return null;
    }

    const hasAny = millimetres.some((value) => value !== null);
    const isComplete = millimetres.every((value) => value !== null);

    return {
      hasAny,
      isComplete,
      dimensions: {
        lensHeight: millimetres[0],
        lensWidth: millimetres[1],
        bridge: millimetres[2],
        totalWidth: millimetres[3],
        templeLength: millimetres[4],
      },
    };
  }

  function extractMillimetres(segment, defaultUnit) {
    const match = segment.match(/(-?\d+(?:[.,]\d+)?)\s*(mm|cm)?/i);
    if (!match) return null;

    const numericValue = Number.parseFloat(match[1].replace(',', '.'));
    if (!Number.isFinite(numericValue) || numericValue < 0) return null;

    const unit = (match[2] || defaultUnit).toLowerCase();
    const millimetres = unit === 'cm' ? numericValue * 10 : numericValue;

    return Math.round(millimetres * 10) / 10;
  }

  function updateDiagram(modal, values) {
    Object.entries(values).forEach(([key, millimetres]) => {
      const element = modal.querySelector(`[data-dimension="${key}"]`);

      if (!element) {
        console.warn(`[glasses-diagram] Elemento para "${key}" não encontrado.`);
        return;
      }

      const centimetres = Math.round((millimetres / 10) * 10) / 10;
      const formattedValue = Number.isInteger(centimetres)
        ? String(centimetres)
        : centimetres.toFixed(1).replace('.', ',');

      element.textContent = `${formattedValue} cm`;
    });
  }

  function showSummaryOnly(wrapper, trigger, summary, modal, values) {
    wrapper.classList.add('lens-diagram-wrapper--summary-only');
    trigger.hidden = true;
    modal.remove();
    moveSummaryBelowOption(wrapper);

    const summaryText = formatSummary(wrapper, values.dimensions);
    if (!summaryText) {
      wrapper.hidden = true;
      return;
    }

    summary.textContent = summaryText;
    summary.hidden = false;
  }

  function moveSummaryBelowOption(wrapper) {
    const optionInfo = wrapper.closest('.product-form__option-info');
    const optionContainer = optionInfo?.parentElement;

    if (optionContainer && optionContainer.lastElementChild !== wrapper) {
      optionContainer.appendChild(wrapper);
    }
  }

  function formatSummary(wrapper, values) {
    const labels = {
      lensHeight: wrapper.dataset.labelLensHeight,
      lensWidth: wrapper.dataset.labelLensWidth,
      bridge: wrapper.dataset.labelBridge,
      totalWidth: wrapper.dataset.labelTotalWidth,
      templeLength: wrapper.dataset.labelTempleLength,
    };

    const parts = Object.entries(values)
      .filter(([, millimetres]) => millimetres !== null)
      .map(([key, millimetres]) => `${capitalize(labels[key] || key)}: ${formatCentimetres(millimetres)}`);

    if (!parts.length) return '';

    return `${wrapper.dataset.summaryPrefix || 'Sunglasses dimensions'} - ${parts.join('; ')}`;
  }

  function formatCentimetres(millimetres) {
    const centimetres = Math.round((millimetres / 10) * 10) / 10;
    const formattedValue = Number.isInteger(centimetres)
      ? String(centimetres)
      : centimetres.toFixed(1).replace('.', ',');

    return `${formattedValue} cm`;
  }

  function capitalize(text) {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  function trapFocus(event, container) {
    const focusableElements = [...container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => !element.hidden && element.offsetParent !== null);

    if (focusableElements.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
